import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Security,
  Home,
  Edit,
  Delete,
  Storage,
  Settings,
  Refresh,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface LlmProvider {
  provider_id: number;
  provider_name: string;
  display_name: string;
  provider_type: string;
  api_key: string;
  api_endpoint?: string;
  api_version?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  model_count: number;
}

interface LlmModel {
  model_id: number;
  provider_id: number;
  model_name: string;
  display_name: string;
  deployment_name: string;
  release: string;
  context_window?: number;
  max_output_tokens?: number;
  input_price_per_1m_tokens?: number;
  output_price_per_1m_tokens?: number;
  supports_streaming: boolean;
  supports_json_mode: boolean;
  supports_function_calling: boolean;
  supports_vision: boolean;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  provider_name: string;
  provider_display_name: string;
  provider_type: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`llm-tabpanel-${index}`}
      aria-labelledby={`llm-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PROVIDER_TYPES = ['anthropic', 'openai', 'google', 'azure-ai-foundry'];

// Helper function to mask API key (show first 3 and last 3 characters)
const maskApiKey = (apiKey: string): string => {
  if (!apiKey || apiKey.length <= 6) return apiKey;
  const first3 = apiKey.substring(0, 3);
  const last3 = apiKey.substring(apiKey.length - 3);
  const maskedLength = apiKey.length - 6;
  const masked = '*'.repeat(Math.min(maskedLength, 20)); // Cap at 20 asterisks for readability
  return `${first3}${masked}${last3}`;
};

export const LLMManagementPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Provider dialogs
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LlmProvider | null>(null);
  const [providerFormData, setProviderFormData] = useState({
    provider_name: '',
    display_name: '',
    provider_type: 'anthropic',
    api_key: '',
    api_endpoint: '',
    api_version: '',
    is_active: true,
  });

  // Model dialogs
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<LlmModel | null>(null);
  const [modelFormData, setModelFormData] = useState({
    provider_id: '',
    model_name: '',
    display_name: '',
    deployment_name: '',
    release: '',
    context_window: '',
    max_output_tokens: '',
    input_price_per_1m_tokens: '',
    output_price_per_1m_tokens: '',
    supports_streaming: true,
    supports_json_mode: false,
    supports_function_calling: false,
    supports_vision: false,
    is_default: false,
    is_active: true,
  });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'provider' | 'model'; id: number; name: string } | null>(null);

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have system.admin permission (after loading completes)
  if (!user?.permissions?.includes('system.admin')) {
    return <Navigate to="/" replace />;
  }

  // Fetch providers
  const { data: providersData, isLoading: providersLoading, refetch: refetchProviders } = useQuery({
    queryKey: ['llmProviders'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-providers`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch LLM providers');
      }
      return await response.json();
    }
  });

  // Fetch models
  const { data: modelsData, isLoading: modelsLoading, refetch: refetchModels } = useQuery({
    queryKey: ['llmModels'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-models`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch LLM models');
      }
      return await response.json();
    }
  });

  // Create provider mutation
  const createProviderMutation = useMutation({
    mutationFn: async (data: typeof providerFormData) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-providers/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to create LLM provider');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchProviders();
      setProviderDialogOpen(false);
      resetProviderForm();
    },
  });

  // Update provider mutation
  const updateProviderMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof providerFormData }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-providers/${id}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to update LLM provider');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchProviders();
      setProviderDialogOpen(false);
      setEditingProvider(null);
      resetProviderForm();
    },
  });

  // Delete provider mutation
  const deleteProviderMutation = useMutation({
    mutationFn: async (providerId: number) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-providers/${providerId}/delete`, {
        method: 'DELETE',
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to delete LLM provider');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchProviders();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    },
  });

  // Create model mutation
  const createModelMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-models/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          provider_id: parseInt(data.provider_id),
          context_window: data.context_window ? parseInt(data.context_window) : null,
          max_output_tokens: data.max_output_tokens ? parseInt(data.max_output_tokens) : null,
          input_price_per_1m_tokens: data.input_price_per_1m_tokens ? parseFloat(data.input_price_per_1m_tokens) : null,
          output_price_per_1m_tokens: data.output_price_per_1m_tokens ? parseFloat(data.output_price_per_1m_tokens) : null,
        }),
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to create LLM model');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchModels();
      refetchProviders(); // Refresh providers to update model counts
      setModelDialogOpen(false);
      resetModelForm();
    },
  });

  // Update model mutation
  const updateModelMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-models/${id}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          provider_id: parseInt(data.provider_id),
          context_window: data.context_window ? parseInt(data.context_window) : null,
          max_output_tokens: data.max_output_tokens ? parseInt(data.max_output_tokens) : null,
          input_price_per_1m_tokens: data.input_price_per_1m_tokens ? parseFloat(data.input_price_per_1m_tokens) : null,
          output_price_per_1m_tokens: data.output_price_per_1m_tokens ? parseFloat(data.output_price_per_1m_tokens) : null,
        }),
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to update LLM model');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchModels();
      refetchProviders(); // Refresh providers to update model counts
      setModelDialogOpen(false);
      setEditingModel(null);
      resetModelForm();
    },
  });

  // Delete model mutation
  const deleteModelMutation = useMutation({
    mutationFn: async (modelId: number) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/llm-models/${modelId}/delete`, {
        method: 'DELETE',
      });
      if (!response || !response.ok) {
        const errorData = response ? await response.json() : { error: 'No response from server' };
        throw new Error(errorData.error || 'Failed to delete LLM model');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchModels();
      refetchProviders(); // Refresh providers to update model counts
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    },
  });

  const providers = providersData?.data || [];
  const models = modelsData?.data || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    if (tabValue === 0) {
      refetchProviders();
    } else {
      refetchModels();
    }
  };

  const resetProviderForm = () => {
    setProviderFormData({
      provider_name: '',
      display_name: '',
      provider_type: 'anthropic',
      api_key: '',
      api_endpoint: '',
      api_version: '',
      is_active: true,
    });
  };

  const resetModelForm = () => {
    setModelFormData({
      provider_id: '',
      model_name: '',
      display_name: '',
      deployment_name: '',
      release: '',
      context_window: '',
      max_output_tokens: '',
      input_price_per_1m_tokens: '',
      output_price_per_1m_tokens: '',
      supports_streaming: true,
      supports_json_mode: false,
      supports_function_calling: false,
      supports_vision: false,
      is_default: false,
      is_active: true,
    });
  };

  const handleCreateProvider = () => {
    resetProviderForm();
    setEditingProvider(null);
    setProviderDialogOpen(true);
  };

  const handleEditProvider = (provider: LlmProvider) => {
    setEditingProvider(provider);
    setProviderFormData({
      provider_name: provider.provider_name,
      display_name: provider.display_name,
      provider_type: provider.provider_type,
      api_key: provider.api_key,
      api_endpoint: provider.api_endpoint || '',
      api_version: provider.api_version || '',
      is_active: provider.is_active,
    });
    setProviderDialogOpen(true);
  };

  const handleDeleteProvider = (provider: LlmProvider) => {
    setItemToDelete({ type: 'provider', id: provider.provider_id, name: provider.display_name });
    setDeleteDialogOpen(true);
  };

  const handleCreateModel = () => {
    resetModelForm();
    setEditingModel(null);
    setModelDialogOpen(true);
  };

  const handleEditModel = (model: LlmModel) => {
    setEditingModel(model);
    setModelFormData({
      provider_id: model.provider_id.toString(),
      model_name: model.model_name,
      display_name: model.display_name,
      deployment_name: model.deployment_name,
      release: model.release,
      context_window: model.context_window?.toString() || '',
      max_output_tokens: model.max_output_tokens?.toString() || '',
      input_price_per_1m_tokens: model.input_price_per_1m_tokens?.toString() || '',
      output_price_per_1m_tokens: model.output_price_per_1m_tokens?.toString() || '',
      supports_streaming: model.supports_streaming,
      supports_json_mode: model.supports_json_mode,
      supports_function_calling: model.supports_function_calling,
      supports_vision: model.supports_vision,
      is_default: model.is_default,
      is_active: model.is_active,
    });
    setModelDialogOpen(true);
  };

  const handleDeleteModel = (model: LlmModel) => {
    setItemToDelete({ type: 'model', id: model.model_id, name: model.display_name });
    setDeleteDialogOpen(true);
  };

  const handleSaveProvider = () => {
    if (editingProvider) {
      updateProviderMutation.mutate({ id: editingProvider.provider_id, data: providerFormData });
    } else {
      createProviderMutation.mutate(providerFormData);
    }
  };

  const handleSaveModel = () => {
    if (editingModel) {
      updateModelMutation.mutate({ id: editingModel.model_id, data: modelFormData });
    } else {
      createModelMutation.mutate(modelFormData);
    }
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      if (itemToDelete.type === 'provider') {
        deleteProviderMutation.mutate(itemToDelete.id);
      } else {
        deleteModelMutation.mutate(itemToDelete.id);
      }
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Settings sx={{ mr: 0.5 }} fontSize="inherit" />
            LLM Providers & Models
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            LLM Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage LLM providers and models for AI-powered features
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={1}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="llm management tabs">
              <Tab icon={<Storage />} label="Providers" iconPosition="start" />
              <Tab icon={<Settings />} label="Models" iconPosition="start" />
            </Tabs>
            <Box sx={{ pr: 2 }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={
                    (tabValue === 0 && providersLoading) ||
                    (tabValue === 1 && modelsLoading)
                  }
                >
                  <Refresh
                    sx={{
                      animation: (
                        (tabValue === 0 && providersLoading) ||
                        (tabValue === 1 && modelsLoading)
                      ) ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Providers Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateProvider}
              >
                Add New Provider
              </Button>
            </Box>
            {providersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Provider Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Endpoint</TableCell>
                      <TableCell>Models</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {providers.map((provider: LlmProvider) => (
                      <TableRow
                        key={provider.provider_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditProvider(provider)}
                      >
                        <TableCell>{provider.display_name}</TableCell>
                        <TableCell>{provider.provider_name}</TableCell>
                        <TableCell>
                          <Chip label={provider.provider_type} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={provider.api_endpoint || '-'}
                        >
                          {provider.api_endpoint || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip label={provider.model_count} size="small" color="info" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={provider.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={provider.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit Provider">
                            <IconButton onClick={() => handleEditProvider(provider)} size="small">
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Provider">
                            <IconButton onClick={() => handleDeleteProvider(provider)} size="small" color="error">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Models Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateModel}
              >
                Add New Model
              </Button>
            </Box>
            {modelsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Model Name</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Deployment Name</TableCell>
                      <TableCell>Context Window</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {models.map((model: LlmModel) => (
                      <TableRow
                        key={model.model_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditModel(model)}
                      >
                        <TableCell>{model.display_name}</TableCell>
                        <TableCell>{model.model_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={model.provider_display_name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{model.deployment_name}</TableCell>
                        <TableCell>{model.context_window?.toLocaleString() || '-'}</TableCell>
                        <TableCell>
                          {model.is_default && (
                            <Chip label="Default" size="small" color="warning" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={model.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={model.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit Model">
                            <IconButton onClick={() => handleEditModel(model)} size="small">
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Model">
                            <IconButton onClick={() => handleDeleteModel(model)} size="small" color="error">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Paper>

        {/* Provider Dialog */}
        <Dialog open={providerDialogOpen} onClose={() => {
          setProviderDialogOpen(false);
          setEditingProvider(null);
          resetProviderForm();
        }} maxWidth="md" fullWidth>
          <DialogTitle>{editingProvider ? 'Edit' : 'Create'} LLM Provider</DialogTitle>
          <DialogContent>
            {createProviderMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createProviderMutation.error?.message || 'Failed to save provider'}
              </Alert>
            )}
            {updateProviderMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateProviderMutation.error?.message || 'Failed to update provider'}
              </Alert>
            )}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Provider Name"
                value={providerFormData.provider_name}
                onChange={(e) => setProviderFormData({ ...providerFormData, provider_name: e.target.value })}
                fullWidth
                required
                helperText="Unique identifier (e.g., 'azure-foundry-main')"
              />
              <TextField
                label="Display Name"
                value={providerFormData.display_name}
                onChange={(e) => setProviderFormData({ ...providerFormData, display_name: e.target.value })}
                fullWidth
                required
                helperText="User-friendly name (e.g., 'Azure AI Foundry')"
              />
              <FormControl fullWidth required>
                <InputLabel>Provider Type</InputLabel>
                <Select
                  value={providerFormData.provider_type}
                  label="Provider Type"
                  onChange={(e: SelectChangeEvent) => setProviderFormData({ ...providerFormData, provider_type: e.target.value })}
                >
                  {PROVIDER_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <TextField
                  label="API Key"
                  value={providerFormData.api_key}
                  onChange={(e) => setProviderFormData({ ...providerFormData, api_key: e.target.value })}
                  fullWidth
                  required
                  type="password"
                  helperText="API key for authentication"
                />
                {editingProvider && editingProvider.api_key && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Current key: {maskApiKey(editingProvider.api_key)}
                  </Typography>
                )}
              </Box>
              <TextField
                label="API Endpoint"
                value={providerFormData.api_endpoint}
                onChange={(e) => setProviderFormData({ ...providerFormData, api_endpoint: e.target.value })}
                fullWidth
                helperText="Optional custom API endpoint URL"
              />
              <TextField
                label="API Version"
                value={providerFormData.api_version}
                onChange={(e) => setProviderFormData({ ...providerFormData, api_version: e.target.value })}
                fullWidth
                helperText="Optional API version string"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={providerFormData.is_active}
                    onChange={(e) => setProviderFormData({ ...providerFormData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setProviderDialogOpen(false);
              setEditingProvider(null);
              resetProviderForm();
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveProvider}
              disabled={
                !providerFormData.provider_name ||
                !providerFormData.display_name ||
                !providerFormData.api_key ||
                createProviderMutation.isPending ||
                updateProviderMutation.isPending
              }
            >
              {createProviderMutation.isPending || updateProviderMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                editingProvider ? 'Update Provider' : 'Create Provider'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Model Dialog */}
        <Dialog open={modelDialogOpen} onClose={() => {
          setModelDialogOpen(false);
          setEditingModel(null);
          resetModelForm();
        }} maxWidth="md" fullWidth>
          <DialogTitle>{editingModel ? 'Edit' : 'Create'} LLM Model</DialogTitle>
          <DialogContent>
            {createModelMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createModelMutation.error?.message || 'Failed to save model'}
              </Alert>
            )}
            {updateModelMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateModelMutation.error?.message || 'Failed to update model'}
              </Alert>
            )}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Row 1: Provider and Model Name */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={modelFormData.provider_id}
                    label="Provider"
                    onChange={(e: SelectChangeEvent) => setModelFormData({ ...modelFormData, provider_id: e.target.value })}
                  >
                    {providers.map((provider: LlmProvider) => (
                      <MenuItem key={provider.provider_id} value={provider.provider_id.toString()}>
                        {provider.display_name} ({provider.provider_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Model Name"
                  value={modelFormData.model_name}
                  onChange={(e) => setModelFormData({ ...modelFormData, model_name: e.target.value })}
                  fullWidth
                  required
                  helperText="Model identifier (e.g., 'claude-3-5-sonnet-20241022')"
                />
              </Box>

              {/* Row 2: Display Name, Deployment Name, and Release */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Display Name"
                  value={modelFormData.display_name}
                  onChange={(e) => setModelFormData({ ...modelFormData, display_name: e.target.value })}
                  fullWidth
                  required
                  helperText="User-friendly name (e.g., 'Claude 3.5 Sonnet')"
                />
                <TextField
                  label="Deployment Name"
                  value={modelFormData.deployment_name}
                  onChange={(e) => setModelFormData({ ...modelFormData, deployment_name: e.target.value })}
                  fullWidth
                  helperText="Optional deployment identifier"
                />
                <TextField
                  label="Release"
                  value={modelFormData.release}
                  onChange={(e) => setModelFormData({ ...modelFormData, release: e.target.value })}
                  fullWidth
                  required
                  helperText="Version (e.g., '20241022')"
                />
              </Box>

              {/* Row 3: Context Window (full width) */}
              <TextField
                label="Context Window"
                value={modelFormData.context_window}
                onChange={(e) => setModelFormData({ ...modelFormData, context_window: e.target.value })}
                fullWidth
                type="number"
                helperText="Maximum context length in tokens"
              />

              {/* Row 4: Max Output Tokens, Input Price, and Output Price */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Max Output Tokens"
                  value={modelFormData.max_output_tokens}
                  onChange={(e) => setModelFormData({ ...modelFormData, max_output_tokens: e.target.value })}
                  fullWidth
                  type="number"
                  helperText="Maximum output tokens"
                />
                <TextField
                  label="Input Price per 1M Tokens (USD)"
                  value={modelFormData.input_price_per_1m_tokens}
                  onChange={(e) => setModelFormData({ ...modelFormData, input_price_per_1m_tokens: e.target.value })}
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.000001", min: "0" }}
                  helperText="Input price (e.g., 3.00)"
                />
                <TextField
                  label="Output Price per 1M Tokens (USD)"
                  value={modelFormData.output_price_per_1m_tokens}
                  onChange={(e) => setModelFormData({ ...modelFormData, output_price_per_1m_tokens: e.target.value })}
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.000001", min: "0" }}
                  helperText="Output price (e.g., 15.00)"
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.supports_streaming}
                      onChange={(e) => setModelFormData({ ...modelFormData, supports_streaming: e.target.checked })}
                    />
                  }
                  label="Supports Streaming"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.supports_json_mode}
                      onChange={(e) => setModelFormData({ ...modelFormData, supports_json_mode: e.target.checked })}
                    />
                  }
                  label="Supports JSON Mode"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.supports_function_calling}
                      onChange={(e) => setModelFormData({ ...modelFormData, supports_function_calling: e.target.checked })}
                    />
                  }
                  label="Supports Function Calling"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.supports_vision}
                      onChange={(e) => setModelFormData({ ...modelFormData, supports_vision: e.target.checked })}
                    />
                  }
                  label="Supports Vision"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.is_default}
                      onChange={(e) => setModelFormData({ ...modelFormData, is_default: e.target.checked })}
                    />
                  }
                  label="Set as Default Model"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={modelFormData.is_active}
                      onChange={(e) => setModelFormData({ ...modelFormData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setModelDialogOpen(false);
              setEditingModel(null);
              resetModelForm();
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveModel}
              disabled={
                !modelFormData.provider_id ||
                !modelFormData.model_name ||
                !modelFormData.display_name ||
                !modelFormData.release ||
                createModelMutation.isPending ||
                updateModelMutation.isPending
              }
            >
              {createModelMutation.isPending || updateModelMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                editingModel ? 'Update Model' : 'Create Model'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            {itemToDelete?.type === 'provider' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You can only delete a provider if it has no associated models.
              </Alert>
            )}
            <Typography>
              Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDeleteDialogOpen(false);
              setItemToDelete(null);
            }}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteProviderMutation.isPending || deleteModelMutation.isPending}
            >
              {deleteProviderMutation.isPending || deleteModelMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                'Delete'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
